/**
 * El link a Langfuse de una celda — y, cuando no existe, **por qué no existe**.
 *
 * Hoy `LANGFUSE_BASE_URL` y `LANGFUSE_PROJECT_ID` están vacíos a propósito: no
 * está confirmado si la instancia de Laburen es cloud o self-hosted. La
 * consecuencia visible es que **ningún link de Langfuse existe todavía**, y ese
 * es el estado correcto. Lo que esta pantalla no puede hacer es dibujar un `<a>`
 * a una URL incompleta: un link que cae en una pantalla vacía es peor que no
 * tener link.
 *
 * El sobre del servidor trae el motivo, y son tres distintos con acciones
 * distintas. Por eso el motivo se **renderiza** en vez de colapsarse en un
 * "sin vincular" genérico:
 *
 *  - `config_global` → falta la variable de entorno. Es una sola cosa que
 *    arregla todas las filas de la pantalla, y la acción es ir a Admin.
 *  - `id_del_registro` sin acción → el agente no está desplegado, o la
 *    plataforma no le estampó el prefijo de sesión. **No se arregla en Admin**,
 *    así que no se ofrece un botón que no puede cumplir lo que promete; se
 *    explica qué falta y dónde.
 *
 * La acción de `config_global` lleva a `/admin`, que es solo para admin. Para
 * ops y comercial el botón no aparece: el estado explica el motivo y el pedido
 * queda a la vista para pasárselo a quien puede cargarlo. Mandarlos a una
 * pantalla de acceso denegado sería peor que no ofrecer nada.
 */
import type { AgenteLinks, ModeloLinks, MotivoSinVincular } from '@ops/contracts'
import { useNavigate } from 'react-router-dom'

import { useMe } from '../../lib/queries'
import { ExternalLink } from '../../ui'

export interface LinkLangfuseProps {
  /** El sobre del servidor. `undefined` = el endpoint no acompañó esta entidad. */
  sobre: AgenteLinks | ModeloLinks | undefined
  /** Texto del link cuando existe, ej. "Sus sesiones". */
  texto: string
  /** Motivo por defecto cuando el sobre no trae ninguno. */
  motivoPorDefecto?: MotivoSinVincular | null
}

export function LinkLangfuse({
  sobre,
  texto,
  motivoPorDefecto = null,
}: LinkLangfuseProps) {
  const navegar = useNavigate()
  const me = useMe()
  const esAdmin = me.data?.rol === 'admin'

  const link = sobre?.langfuse ?? null
  if (link !== null) {
    return (
      <ExternalLink link={link} variante="chip">
        {texto}
      </ExternalLink>
    )
  }

  const motivo = sobre?.sinVincular ?? motivoPorDefecto
  const accion = motivo?.accion ?? null

  return (
    <span
      data-testid="langfuse-sin-vincular"
      className="flex min-w-0 flex-col items-start gap-0.5"
    >
      <ExternalLink
        link={null}
        sistemaFaltante="Langfuse"
        {...(accion !== null && esAdmin
          ? { onVincular: () => navegar(accion.ruta), textoVincular: accion.label }
          : {})}
      />
      {motivo !== null && motivo !== undefined ? (
        <span className="max-w-prosa text-micro text-muted-strong">
          {motivo.mensaje}
          {motivo.envVar !== null ? (
            <>
              {' '}
              <code className="rounded border border-border bg-n100 px-1 py-px font-mono text-micro text-text">
                {motivo.envVar}
              </code>
            </>
          ) : null}
        </span>
      ) : null}
    </span>
  )
}
